import { useCallback, memo } from 'react';
import { cn } from '@divlab/divanui';

import * as CatalogFiltrator from '@Stores/CatalogFiltrator';
import useMedias from '@Hooks/useMedias';
import useTranslation from '@Queries/useTranslation';
import FilterSelectedValues from '@Components/FilterSelectedValues';
import Button from '@UI/Button';
import Labels from '../common/Labels';
import AllFiltersButton from '../AllFiltersButton';
import styles from './FavoritesFilters.module.css';

import type { FC, HTMLAttributes, MouseEvent } from 'react';
import type { GroupData } from '../typings';

export interface WrapperFiltersProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  wrapper?: boolean;
}

export interface FiltersProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  count?: number;
  groups?: GroupData[];
  onOpen?: (e: MouseEvent, id: string) => void;
}

const WrapperFilters: FC<WrapperFiltersProps> = (props) => {
  const { wrapper, className, children } = props;

  return wrapper ? (
    <div className={cn(styles.wrapper, className)}>{children}</div>
  ) : (
    <>{children}</>
  );
};

const FavoritesFilters: FC<FiltersProps> = (props) => {
  const { className, count, groups = [], onOpen, ...restProps } = props;
  const { t } = useTranslation();
  const { isDesktop, isMobile } = useMedias();
  const filtrator = CatalogFiltrator.useFiltrator();
  const appliedValues = CatalogFiltrator.useAppliedValues();

  const handleOpen = useCallback(
    (e: MouseEvent, selectedFilterId: string) => {
      if (onOpen) onOpen(e, selectedFilterId);
    },
    [onOpen],
  );

  return (
    <div {...restProps} className={cn(styles.filters, className)}>
      <WrapperFilters wrapper={!isMobile} className={styles.actions}>
        {filtrator.filters && (
          <div className={styles.buttons}>
            <AllFiltersButton onClick={(e) => handleOpen(e, 'all')} />
          </div>
        )}

        {typeof count === 'number' && (
          <div className={styles.count}>
            {t('ui.filters.found')}
            <span className='ProductRange'>{count}</span>
          </div>
        )}
      </WrapperFilters>

      <WrapperFilters wrapper={!isDesktop} className={styles.rightContent}>
        <Button className={styles.compareButton} theme='transparent' to='/compare'>
          {t('ui.compare')}
        </Button>
        <Labels className={styles.labels} groups={groups} filterView='default' />
      </WrapperFilters>

      {appliedValues.length > 0 && (
        <FilterSelectedValues
          className={styles.selectedValues}
          values={appliedValues}
          filtratorId='catalog'
          scrollable={!isDesktop || isMobile}
        />
      )}
    </div>
  );
};

export default memo(FavoritesFilters);
